import { useRef, useState } from 'react'
import './input.scss'
import { InputTypes } from '../../../lib/enums'
import classNames from 'classnames'
import { useInputState } from './useInputState'
import { validationRules, ValidationRule, processValue } from './validators'

const Input = ({
  id,
  type,
  placeholder,
  label,
  initialValue,
  isDisabled,
  handleDataChange,
  onEventType = 'onBlur',
  isSmall,
  className,
  maxLength,
  maximumNumber,
  minimumNumber,
  required = true,
  hideLableOnInput,
}: InputProps) => {
  const getValidationRules = (): ValidationRule[] => {
    const rules: ValidationRule[] = []

    if (type === InputTypes.Number) {
      rules.push(validationRules.range)
    }

    if (maxLength) rules.push(validationRules.maxLength)
    if (required) rules.push(validationRules.required)

    return rules
  }

  const validationOptions = {
    min: minimumNumber,
    max: maximumNumber,
    maxLength
  }

  const numericPattern = /^[0-9]*$/
  
  const { value, setValue, lastValidValue, setLastValidValue, error, setError, isEscapePressed, handleEscape, resetEscapeState } = useInputState(initialValue ?? '')
  const [isFocused, setIsFocused] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null)

  const shouldShowLabel =
    hideLableOnInput === true ? !value && !isFocused : true;

  const handleInputChange = event => {
    // Avoid handling blur if Escape key was pressed
    if (isEscapePressed) {
      resetEscapeState()
      return
    }

    const newValue = event.target.value
    setValue(newValue)

    if (onEventType === 'onChange') {
      handleValueValidation(newValue, true) // true = commit value immediately
    } else {
      handleValueValidation(newValue, false) // false = just show validation, don't commit
    }
  }

  const handleInputBlur = event => {
    const newValue = event.target.value

    // Avoid handling blur if Escape key was pressed
    if (isEscapePressed) {
      resetEscapeState()
      return
    }

    // Validate and handle the value change
    handleValueValidation(newValue, true)
  }

  const handleValueValidation = (newValue: string, shouldCommit: boolean) => {
    const rules = getValidationRules()
    const result = processValue(newValue, rules, validationOptions)

    if (result.isValid) {
      // Valid input: clear error and update if committing
      setError(null)
      if (shouldCommit) {
        setLastValidValue(result.value)
        handleDataChange(result.value, true)
      }
    } else {
      // Invalid input: show error
      setError(result.error)
      if (shouldCommit) {
        // On blur with invalid value: revert to last valid value and clear error
        setValue(lastValidValue)
        setError(null)
      }
    }
  }

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      // Revert to last valid value and clear error
      setValue(lastValidValue)
      setError(null)
      handleEscape()
    }

    if (event.key === 'Enter') {
      //call the blur event to save the new value
      inputRef.current.blur()
    }
  }

  const handleBeforeInput = event => {
    if (type !== InputTypes.Number) return

    const inputData = event.data
    if (!inputData) return

    if (!numericPattern.test(inputData)) {
      event.preventDefault() // Prevent invalid characters from being entered
    } else {
      setError(null) // Clear any existing error if the input characters are valid
    }
  }

  const handleBeforePaste = event => {
    if (type !== InputTypes.Number) return

    const pastedData = event.clipboardData.getData('text/plain')

    if (!numericPattern.test(pastedData)) {
      event.preventDefault() // Prevent invalid characters from being pasted
      setError(`Only numbers are allowed - pasted value: "${pastedData}"`)
      setTimeout(() => setError(null), 3000) // Clear error after a short delay
    }
  }

  return (
    <div
      className={classNames(
        'msds-input',
        type === InputTypes.Number && 'msds-input--numeric',
        className,
        isSmall && 'msds-input--small',
        !label && 'msds-input--no-label',
        error && 'msds-input--error'
      )}>
      <div className="msds-input__icon-wrapper">
        <input
          ref={inputRef}
          id={id}
          autoComplete="off"
          type={type ?? InputTypes.Text}
          className="msds-input__text-input"
          placeholder={placeholder}
          value={value}
          disabled={isDisabled}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onBeforeInput={handleBeforeInput}
          onPaste={handleBeforePaste}
          maxLength={maxLength}
          max={maximumNumber}
          min={minimumNumber}
        />
        {label && shouldShowLabel && (
          <label htmlFor={id} className="msds-input__label">
            {label}
          </label>
        )}
      </div>
      {error && (
        <div className="msds-input__validation-message">
          <div className="validation-message-inline">{error}</div>
        </div>
      )}
    </div>
  )
}

export default Input

type InputProps = {
  id?: string
  type?: InputTypes
  placeholder?: string
  label?: string
  initialValue?: string
  isDisabled?: boolean
  initialMessage?: { info?: string; validation: string }
  data?: { [key: string]: any }
  onEventType?: 'onChange' | 'onBlur'
  handleDataChange?: (newValue: any, isValid?: boolean) => void
  isSmall?: boolean
  className?: string
  maxLength?: number
  maximumNumber?: number
  minimumNumber?: number
  required?: boolean
  hideLableOnInput?: boolean
  onBlur?: () => void
  onFocus?: () => void
}
